/**
 * MEVIA v4.0 - Mass Import Component
 * UI for bulk importing employees and organizational structure
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Upload, Download, FileSpreadsheet, Users, FolderTree, 
  Briefcase, Layers, AlertCircle, CheckCircle, XCircle,
  RefreshCw, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const TEXTS = {
  es: {
    title: 'Importación Masiva',
    subtitle: 'Carga empleados y estructura organizacional desde Excel',
    
    // Tabs
    employees: 'Empleados',
    areas: 'Áreas',
    positions: 'Puestos',
    levels: 'Niveles',
    
    // Actions
    downloadTemplate: 'Descargar Plantilla',
    selectFile: 'Seleccionar Archivo',
    validate: 'Validar',
    import: 'Importar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    
    // Status
    ready: 'Listo para importar',
    validating: 'Validando...',
    importing: 'Importando...',
    completed: 'Completado',
    
    // Results
    totalRows: 'Total de filas',
    successful: 'Exitosas',
    failed: 'Fallidas',
    warnings: 'Advertencias',
    errors: 'Errores',
    preview: 'Vista previa',
    row: 'Fila',
    error: 'Error',
    
    // Messages
    validationSuccess: 'Archivo validado correctamente',
    validationErrors: 'El archivo tiene errores que deben corregirse',
    importSuccess: 'Importación completada',
    noFile: 'Selecciona un archivo',
    fileSelected: 'Archivo seleccionado',
    
    // Instructions
    step1: '1. Descarga la plantilla',
    step2: '2. Llena los datos',
    step3: '3. Sube el archivo',
    step4: '4. Valida e importa',
    
    templateDesc: 'La plantilla incluye las columnas requeridas y una hoja de instrucciones.',
    requiredFields: 'Campos obligatorios marcados en amarillo',
  },
  en: {
    title: 'Mass Import',
    subtitle: 'Upload employees and organizational structure from Excel',
    
    // Tabs
    employees: 'Employees',
    areas: 'Areas',
    positions: 'Positions',
    levels: 'Levels',
    
    // Actions
    downloadTemplate: 'Download Template',
    selectFile: 'Select File',
    validate: 'Validate',
    import: 'Import',
    cancel: 'Cancel',
    close: 'Close',
    
    // Status
    ready: 'Ready to import',
    validating: 'Validating...',
    importing: 'Importing...',
    completed: 'Completed',
    
    // Results
    totalRows: 'Total rows',
    successful: 'Successful',
    failed: 'Failed',
    warnings: 'Warnings',
    errors: 'Errors',
    preview: 'Preview',
    row: 'Row',
    error: 'Error',
    
    // Messages
    validationSuccess: 'File validated successfully',
    validationErrors: 'File has errors that must be fixed',
    importSuccess: 'Import completed',
    noFile: 'Select a file',
    fileSelected: 'File selected',
    
    // Instructions
    step1: '1. Download template',
    step2: '2. Fill in the data',
    step3: '3. Upload the file',
    step4: '4. Validate and import',
    
    templateDesc: 'The template includes required columns and an instructions sheet.',
    requiredFields: 'Required fields marked in yellow',
  }
};

const MassImport = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;
  const empresaId = user?.empresa_id;

  const [activeTab, setActiveTab] = useState('employees');
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, validating, validated, importing, completed
  const [importStatus, setImportStatus] = useState(null);
  const [forcePasswordChange, setForcePasswordChange] = useState(true); // New state for password change checkbox

  useEffect(() => {
    loadImportStatus();
  }, []);

  useEffect(() => {
    // Reset state when tab changes
    setSelectedFile(null);
    setValidationResult(null);
    setImportResult(null);
    setStatus('idle');
  }, [activeTab]);

  const loadImportStatus = async () => {
    try {
      const response = await axios.get('/import/status');
      if (response.data.success) {
        setImportStatus(response.data);
      }
    } catch (error) {
      logger.error('Error loading import status:', error);
    }
  };

  const downloadTemplate = async (type) => {
    try {
      const response = await axios.get(`/import/templates/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_import_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? 'Plantilla descargada' : 'Template downloaded');
    } catch (error) {
      logger.error('Error downloading template:', error);
      toast.error(texts.error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setValidationResult(null);
      setImportResult(null);
      setStatus('idle');
    }
  };

  const validateFile = async () => {
    if (!selectedFile) {
      toast.error(texts.noFile);
      return;
    }

    setStatus('validating');
    setValidationResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(
        `/import/validate/${activeTab}/${empresaId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setValidationResult(response.data);
      setStatus(response.data.is_valid ? 'validated' : 'idle');
      
      if (response.data.is_valid) {
        toast.success(texts.validationSuccess);
      } else {
        toast.error(texts.validationErrors);
      }
    } catch (error) {
      logger.error('Validation error:', error);
      toast.error(error.response?.data?.detail || 'Validation failed');
      setStatus('idle');
    }
  };

  const importFile = async () => {
    if (!selectedFile || status !== 'validated') {
      return;
    }

    setStatus('importing');
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    
    // Add force_password_change parameter for employees import
    if (activeTab === 'employees') {
      formData.append('force_password_change', forcePasswordChange.toString());
    }

    try {
      const response = await axios.post(
        `/import/${activeTab}/${empresaId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResult(response.data);
      setStatus('completed');
      
      toast.success(`${texts.importSuccess}: ${response.data.successful}/${response.data.total_rows}`);
    } catch (error) {
      logger.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
      setStatus('validated');
    }
  };

  const getTemplateConfig = () => {
    const configs = {
      employees: { icon: Users, color: 'bg-blue-100 text-blue-700' },
      areas: { icon: FolderTree, color: 'bg-green-100 text-green-700' },
      positions: { icon: Briefcase, color: 'bg-purple-100 text-purple-700' },
      levels: { icon: Layers, color: 'bg-orange-100 text-orange-700' },
    };
    return configs[activeTab] || configs.employees;
  };

  const config = getTemplateConfig();
  const Icon = config.icon;

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="employees" className="gap-2">
            <Users className="h-4 w-4" />
            {texts.employees}
          </TabsTrigger>
          <TabsTrigger value="areas" className="gap-2">
            <FolderTree className="h-4 w-4" />
            {texts.areas}
          </TabsTrigger>
          <TabsTrigger value="positions" className="gap-2">
            <Briefcase className="h-4 w-4" />
            {texts.positions}
          </TabsTrigger>
          <TabsTrigger value="levels" className="gap-2">
            <Layers className="h-4 w-4" />
            {texts.levels}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-teal-600" />
                  {language === 'es' ? 'Instrucciones' : 'Instructions'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Badge className="bg-teal-100 text-teal-700">1</Badge>
                    <div>
                      <p className="font-medium">{texts.step1}</p>
                      <p className="text-sm text-gray-500">{texts.templateDesc}</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2"
                        onClick={() => downloadTemplate(activeTab)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {texts.downloadTemplate}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Badge className="bg-teal-100 text-teal-700">2</Badge>
                    <div>
                      <p className="font-medium">{texts.step2}</p>
                      <p className="text-sm text-gray-500">{texts.requiredFields}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Badge className="bg-teal-100 text-teal-700">3</Badge>
                    <div>
                      <p className="font-medium">{texts.step3}</p>
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileSelect}
                        className="mt-2 text-sm"
                        id="file-upload"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Badge className="bg-teal-100 text-teal-700">4</Badge>
                    <div>
                      <p className="font-medium">{texts.step4}</p>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={validateFile}
                          disabled={!selectedFile || status === 'validating'}
                        >
                          {status === 'validating' ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          {texts.validate}
                        </Button>
                        <Button 
                          size="sm"
                          onClick={importFile}
                          disabled={status !== 'validated'}
                        >
                          {status === 'importing' ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          {texts.import}
                        </Button>
                      </div>
                      {/* Password change checkbox - only for employees */}
                      {activeTab === 'employees' && (
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={forcePasswordChange}
                            onChange={(e) => setForcePasswordChange(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-600">
                            {language === 'es' 
                              ? 'Forzar cambio de contraseña en primer login' 
                              : 'Force password change on first login'}
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${config.color.split(' ')[1]}`} />
                  {status === 'completed' ? texts.completed : 
                   status === 'validated' ? texts.ready : 
                   status === 'validating' ? texts.validating :
                   status === 'importing' ? texts.importing :
                   texts.fileSelected}
                </CardTitle>
                {selectedFile && (
                  <CardDescription>
                    <FileSpreadsheet className="h-4 w-4 inline mr-2" />
                    {selectedFile.name}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {!selectedFile ? (
                  <div className="text-center py-8 text-gray-500">
                    <Upload className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>{texts.noFile}</p>
                  </div>
                ) : validationResult ? (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">{texts.totalRows}</p>
                        <p className="text-2xl font-bold">{validationResult.total_rows}</p>
                      </div>
                      <div className={`p-3 rounded-lg ${validationResult.is_valid ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className="text-sm text-gray-500">
                          {validationResult.is_valid ? texts.successful : texts.errors}
                        </p>
                        <p className={`text-2xl font-bold ${validationResult.is_valid ? 'text-green-600' : 'text-red-600'}`}>
                          {validationResult.is_valid ? validationResult.valid_rows : validationResult.errors?.length || 0}
                        </p>
                      </div>
                    </div>

                    {/* Errors */}
                    {validationResult.errors?.length > 0 && (
                      <div>
                        <p className="font-medium text-red-600 mb-2 flex items-center gap-2">
                          <XCircle className="h-4 w-4" />
                          {texts.errors} ({validationResult.errors.length})
                        </p>
                        <div className="max-h-48 overflow-y-auto border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">{texts.row}</TableHead>
                                <TableHead>{texts.error}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {validationResult.errors.slice(0, 10).map((err, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-mono">{err.row}</TableCell>
                                  <TableCell className="text-red-600 text-sm">
                                    {err.errors?.join(', ') || err.error}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        {validationResult.errors.length > 10 && (
                          <p className="text-sm text-gray-500 mt-2">
                            ... {language === 'es' ? 'y' : 'and'} {validationResult.errors.length - 10} {language === 'es' ? 'más' : 'more'}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Preview */}
                    {validationResult.preview_data?.length > 0 && validationResult.is_valid && (
                      <div>
                        <p className="font-medium text-gray-700 mb-2">{texts.preview}</p>
                        <div className="max-h-48 overflow-x-auto border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {Object.keys(validationResult.preview_data[0]).map(key => (
                                  <TableHead key={key} className="text-xs">{key}</TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {validationResult.preview_data.map((row, idx) => (
                                <TableRow key={idx}>
                                  {Object.values(row).map((val, vidx) => (
                                    <TableCell key={vidx} className="text-xs">
                                      {val?.toString() || '-'}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : importResult ? (
                  <div className="space-y-4">
                    {/* Import Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-sm text-gray-500">{texts.totalRows}</p>
                        <p className="text-2xl font-bold">{importResult.total_rows}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-sm text-gray-500">{texts.successful}</p>
                        <p className="text-2xl font-bold text-green-600">{importResult.successful}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-sm text-gray-500">{texts.failed}</p>
                        <p className="text-2xl font-bold text-red-600">{importResult.failed}</p>
                      </div>
                    </div>
                    
                    {/* Progress */}
                    <Progress 
                      value={(importResult.successful / importResult.total_rows) * 100} 
                      className="h-2"
                    />
                    
                    {/* Errors */}
                    {importResult.errors?.length > 0 && (
                      <div>
                        <p className="font-medium text-red-600 mb-2">{texts.errors}</p>
                        <div className="max-h-32 overflow-y-auto border rounded-lg p-2">
                          {importResult.errors.slice(0, 5).map((err, idx) => (
                            <p key={idx} className="text-sm text-red-600">
                              {texts.row} {err.row}: {err.error}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Warnings */}
                    {importResult.warnings?.length > 0 && (
                      <div>
                        <p className="font-medium text-amber-600 mb-2">{texts.warnings}</p>
                        <div className="max-h-32 overflow-y-auto border rounded-lg p-2">
                          {importResult.warnings.map((warn, idx) => (
                            <p key={idx} className="text-sm text-amber-600">{warn}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>{language === 'es' ? 'Archivo listo para validar' : 'File ready to validate'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MassImport;
